/**
 * FmDropzone — reusable file-upload dropzone primitive.
 *
 * Owns the dashed upload target + drag/keyboard affordance and the hairline
 * per-file row list (status chip on accepted files, an inline reason row on
 * rejected ones — never a silent drop). Feature pages compose an upload flow
 * without page-local CSS. Client-side type filtering is the caller's job; the
 * primitive only presents rows the caller hands it plus its own render slots.
 *
 */
import React, { useRef } from 'react';
import './FmDropzone.css';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

/**
 * Governed inline-copy renderer for the hint slot: a string hint may embed
 * backtick-delimited inline-code spans (e.g. "Required files: `samples.csv`")
 * which resolve to themed `<code>` elements. This is the sealed rich-text seam —
 * callers express intent with a portable marker, never raw HTML/JSX, and the
 * primitive owns the escaped, DS-styled output. Non-string ReactNode hints pass
 * through untouched.
 */
const renderHint = (hint: React.ReactNode): React.ReactNode => {
  if (typeof hint !== 'string') return hint;
  // Split on backticks: odd-position segments sit between a pair → inline code.
  // Plain-text segments stay bare strings (no key needed); code segments are
  // keyed by their character offset in the source (stable, never the map index).
  const nodes: React.ReactNode[] = [];
  let offset = 0;
  hint.split('`').forEach((segment, position) => {
    if (position % 2 === 1) {
      nodes.push(
        <code key={`code-${offset}`} className="fm-dropzone__code">
          {segment}
        </code>,
      );
    } else {
      nodes.push(segment);
    }
    offset += segment.length + 1;
  });
  return nodes;
};

export interface FmDropzoneRow {
  id: string;
  /** Primary label (file name). */
  name?: React.ReactNode;
  /** Trailing status affordance (e.g. an FmChip). */
  status?: React.ReactNode;
  /** Standalone reason row rendered full-width (rejections). */
  reason?: React.ReactNode;
  rejected?: boolean;
  'data-testid'?: string;
  /** Test id applied to the inline reason row rendered under a named row. */
  reasonTestId?: string;
  role?: string;
}

export interface FmDropzoneProps {
  title: React.ReactNode;
  prompt?: React.ReactNode;
  hint?: React.ReactNode;
  /** Rows to render below the target. Empty → no row list. */
  rows?: FmDropzoneRow[];
  accept?: string;
  multiple?: boolean;
  ariaLabel?: string;
  onFiles: (files: File[]) => void;
  /**
   * When true the target is inert: no click/keyboard/drop affordance, no file
   * input, muted visuals, and `aria-disabled`. Use this instead of rendering an
   * interactive-looking target that silently no-ops when uploads cannot be
   * accepted (e.g. the decode capability is not served yet). The caller should
   * explain *why* it is disabled alongside the dropzone.
   */
  disabled?: boolean;
  inputTestId?: string;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

export const FmDropzone: React.FC<FmDropzoneProps> = ({
  title,
  prompt,
  hint,
  rows = [],
  accept,
  multiple = true,
  ariaLabel,
  onFiles,
  disabled = false,
  inputTestId,
  className,
  'data-testid': dataTestId,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    onFiles(Array.from(fileList));
    // Reset so the same file can be re-uploaded.
    e.target.value = '';
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) onFiles(dropped);
  };

  // Interactive affordance is stripped entirely when disabled: no click/keyboard/
  // drop, no button role, not focusable — an inert target, not one that no-ops.
  const targetInteraction: React.HTMLAttributes<HTMLDivElement> = disabled
    ? { 'aria-disabled': true }
    : {
        onClick: openPicker,
        onKeyDown: handleKeyDown,
        onDrop: handleDrop,
        onDragOver: (e) => e.preventDefault(),
        role: 'button',
        tabIndex: 0,
      };

  return (
    <div
      className={cx('fm-dropzone', disabled && 'fm-dropzone--disabled', className)}
      data-testid={dataTestId}
    >
      <div
        className="fm-dropzone__target"
        {...targetInteraction}
        aria-label={typeof title === 'string' ? title : ariaLabel}
      >
        {disabled ? null : (
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={handleChange}
            hidden
            data-testid={inputTestId}
            aria-hidden="true"
            tabIndex={-1}
          />
        )}
        <div className="fm-dropzone__title">{title}</div>
        {prompt ? <div className="fm-dropzone__prompt">{prompt}</div> : null}
        {hint ? <div className="fm-dropzone__hint">{renderHint(hint)}</div> : null}
      </div>

      {rows.length > 0 ? (
        <ul className="fm-dropzone__rows">
          {rows.map((row) => {
            if (row.reason && !row.name && !row.status) {
              return (
                <li
                  key={row.id}
                  className={cx(
                    'fm-dropzone__row',
                    'fm-dropzone__row--reason',
                    row.rejected && 'fm-dropzone__row--rejected',
                  )}
                  data-testid={row['data-testid']}
                  role={row.role}
                >
                  <span className="fm-dropzone__reason">{row.reason}</span>
                </li>
              );
            }
            return (
              <React.Fragment key={row.id}>
                <li
                  className={cx('fm-dropzone__row', row.rejected && 'fm-dropzone__row--rejected')}
                  data-testid={row['data-testid']}
                  role={row.role}
                >
                  <span className="fm-dropzone__name">{row.name}</span>
                  {row.status}
                </li>
                {row.reason ? (
                  <li
                    className="fm-dropzone__row fm-dropzone__row--reason"
                    data-testid={row.reasonTestId}
                    role={row.role ?? 'alert'}
                  >
                    <span className="fm-dropzone__reason">{row.reason}</span>
                  </li>
                ) : null}
              </React.Fragment>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default FmDropzone;
